<template>
  <div class="h-full flex">
    <!-- Left Sidebar -->
    <PageSidebar
      title="Growth"
      :items="sidebarItems"
      width="w-48"
      bg-class="bg-gray-100"
      :show-collapse-icon="false"
      @item-click="onSidebarClick"
    />

    <!-- Main Content -->
    <div class="flex-1 h-full overflow-y-auto bg-white">
      <div class="px-8 py-6 flex flex-col h-full">
        <!-- Tab Content -->
        <div class="flex-1 min-h-0">
          <transition name="fade" mode="out-in">
          <!-- Directory Tab -->
          <div v-if="activeTab === 'directory'" key="directory">
            <!-- Practice Detail View -->
            <div v-if="selectedPractice">
              <button
                class="mb-4 flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                @click="selectedPractice = null"
              >
                <font-awesome-icon :icon="['fas', 'arrow-left']" class="text-xs" />
                Back to list
              </button>
              <PracticeCard
                :practice="selectedPractice"
                :detail="practiceDetail"
                :detail-loading="practiceDetailLoading"
                :insights="practiceInsights"
                :insights-loading="practiceInsightsLoading"
                :financial="practiceFinancial"
                :financial-loading="practiceFinancialLoading"
                :peer-benchmark="practicePeerBenchmark"
                :peer-loading="practicePeerBenchmarkLoading"
                :in-aco-builder="selectedPractice?.TIN_ID ? isInAcoBuilderRoster(selectedPractice.TIN_ID) : false"
                :tag-display-rules="tagDisplayRules"
                @toggle-aco-builder="toggleAcoBuilderRoster"
                @refetch-peer-benchmark="refetchPeerBenchmarkWithDimensions"
                @tab-change="onPracticeTabChange"
                @filter-by-tag="applyTagFilter"
              />
            </div>
            <!-- Practice List View -->
            <div v-else>
              <PracticeFilter
                :filter="practiceFilter"
                :tag-dimensions="tagDimensions"
                :tag-groups="tagGroups"
                @update:filter="practiceFilter = $event"
                @search="fetchPractices"
                @clear-filter="clearFilter"
                @clear-all="clearFilters"
              />
              <PracticeList
                class="mt-4"
                :practices="practices"
                :loading="practicesLoading"
                :error="practicesError"
                :pagination="practicePagination"
                :selected-pac-id="null"
                :aco-builder-roster="acoBuilder.roster"
                @select="selectPractice"
                @toggle-aco-builder="toggleAcoBuilderRoster"
                @prev-page="prevPracticePage"
                @next-page="nextPracticePage"
              />
            </div>
          </div>

          <!-- Prospects V2 Tab -->
          <div v-else-if="activeTab === 'prospectsV2'" key="prospectsV2">
            <ProspectsV2Tab />
          </div>

          <!-- ACO Builder Tab -->
          <div v-else-if="activeTab === 'acoBuilder'" key="acoBuilder">
            <AcoBuilderTab
              :roster="acoBuilder.roster"
              :year="acoBuilder.year"
              :track="acoBuilder.track"
              :msr-type="acoBuilder.msrType"
              :quality-met="acoBuilder.qualityMet"
              :target-py="acoBuilder.targetPy"
              :projection="acoBuilder.projection"
              :loading="acoBuilder.loading"
              :error="acoBuilder.error"
              :warning="acoBuilder.warning"
              @remove="removeFromAcoBuilderRoster"
              @clear="clearAcoBuilderRoster"
              @update:year="updateAcoBuilderYear"
              @update:track="updateAcoBuilderTrack"
              @update:msr-type="updateAcoBuilderMsrType"
              @update:quality-met="updateAcoBuilderQuality"
              @update:target-py="updateAcoBuilderTargetPy"
              @calculate="calculateProjection"
              @export="exportRoster"
            />
          </div>

          <!-- Graph Tab -->
          <div v-else-if="activeTab === 'graph'" key="graph" class="h-full">
            <GraphTab />
          </div>

          <!-- Archetypes Tab -->
          <div v-else-if="activeTab === 'archetypes'" key="archetypes">
            <PatternExplorerTab
              :patternsData="patternsData"
              :loading="patternsLoading"
              :error="patternsError"
              :selectedItem="selectedItem"
              :ruleEditor="ruleEditor"
              :tagPractices="tagPractices"
              @select-dimension="selectDimension"
              @select-layer2-flag="selectLayer2Flag"
              @select-layer3-archetype="selectLayer3Archetype"
              @start-editing-rule="startEditingRule"
              @cancel-editing-rule="cancelEditingRule"
              @preview-rule="previewRule"
              @save-rule="saveRule"
              @update-expression="ruleEditor.expression = $event"
              @load-more-practices="loadMoreTagPractices"
              @navigate-to-practice="navigateToPractice"
            />
          </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PatternExplorerTab from './tabs/PatternExplorerTab.vue'
import AcoBuilderTab from './tabs/AcoBuilderTab.vue'
import GraphTab from './tabs/GraphTab.vue'
import ProspectsV2Tab from './tabs/ProspectsV2Tab.vue'
import PracticeFilter from './tabs/PracticeFilter.vue'
import PracticeList from './tabs/PracticeList.vue'
import PracticeCard from './tabs/PracticeCard.vue'
import PageSidebar from '@/components/widgetcomponents/library/PageSidebar.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

export default {
  name: 'GrowthDashboard',
  components: {
    PatternExplorerTab,
    AcoBuilderTab,
    GraphTab,
    ProspectsV2Tab,
    PracticeFilter,
    PracticeList,
    PracticeCard,
    PageSidebar,
    FontAwesomeIcon,
  },
  data() {
    return {
      activeTab: 'directory',
      // Practice list tab
      practicesLoading: false,
      practicesError: null,
      practices: [],
      practiceFilter: {
        state: '',
        search: '',
        minBenes: '',
        maxBenes: '',
      },
      // Tag dimensions config (fetched from API on mount)
      tagDimensions: [],
      tagGroups: [],
      tagDisplayRules: [],
      practicePagination: {
        limit: 50,
        offset: 0,
        total: 0,
      },
      selectedPractice: null,
      practiceDetailLoading: false,
      practiceDetail: null,
      practiceInsightsLoading: false,
      practiceInsights: null,
      // Financial assessment
      practiceFinancial: null,
      practiceFinancialLoading: false,
      // Peer benchmark
      practicePeerBenchmark: null,
      practicePeerBenchmarkLoading: false,
      // ACO Builder (roster/year/track restored from localStorage)
      acoBuilder: {
        roster: JSON.parse(localStorage.getItem('aco_builder_roster') || '[]'),
        year: Number(localStorage.getItem('aco_builder_year')) || 2024,
        track: localStorage.getItem('aco_builder_track') || 'BASIC_E',
        msrType: localStorage.getItem('aco_builder_msr_type') || 'variable',
        qualityMet: localStorage.getItem('aco_builder_quality_met') !== 'false',
        targetPy: Number(localStorage.getItem('aco_builder_target_py')) || 2026,
        loading: false,
        error: null,
        warning: null,
        projection: null,
      },
      // Pattern Explorer
      patternsLoading: false,
      patternsError: null,
      patternsData: null,
      selectedItem: null,
      ruleEditor: {
        editing: false,
        tagId: null,
        expression: '',
        loading: false,
        error: null,
        preview: null,
      },
      tagPractices: {
        loading: false,
        practices: [],
        total: 0,
        offset: 0,
        limit: 20,
      },
    }
  },
  mounted() {
    this.fetchTagDimensions()
    this.fetchPractices()
  },
  computed: {
    pageTitle() {
      const titles = { directory: 'Directory', prospectsV2: 'Prospects V2', acoBuilder: 'ACO Builder', graph: 'Graph', archetypes: 'Archetypes' }
      return titles[this.activeTab] || 'Growth'
    },
    sidebarItems() {
      const iconClass = 'text-gray-500 text-[13px] mr-1'
      return [
        { id: 'prospectsV2', label: 'Overview', active: this.activeTab === 'prospectsV2', icon: ['fas', 'bolt'], iconClass },
        { id: 'directory', label: 'Directory', active: this.activeTab === 'directory', icon: ['fas', 'search'], iconClass },
        {
          id: 'acoBuilder',
          label: 'ACO Builder',
          active: this.activeTab === 'acoBuilder',
          icon: ['fas', 'cubes'],
          iconClass,
          badge: this.acoBuilder.roster.length > 0 ? String(this.acoBuilder.roster.length) : null,
        },
        { id: 'graph', label: 'Graph', active: this.activeTab === 'graph', icon: ['fas', 'diagram-project'], iconClass },
        { id: 'archetypes', label: 'Archetypes', active: this.activeTab === 'archetypes', icon: ['fas', 'fingerprint'], iconClass },
      ]
    },
  },
  watch: {
    activeTab(newTab) {
      if (newTab === 'directory' && this.practices.length === 0) {
        this.fetchPractices()
      }
      if (newTab === 'archetypes' && !this.patternsData) {
        this.fetchPatterns()
      }
    },
    selectedPractice(practice) {
      if (practice) {
        this.practiceFinancial = null
        this.practicePeerBenchmark = null
        this.fetchPracticeDetail(practice.PAC_ID)
        this.fetchPracticeInsights(practice.PAC_ID)
      }
    },
    'acoBuilder.roster': {
      deep: true,
      handler(roster) { localStorage.setItem('aco_builder_roster', JSON.stringify(roster)) }
    },
    'acoBuilder.year'(year) { localStorage.setItem('aco_builder_year', String(year)) },
    'acoBuilder.track'(track) { localStorage.setItem('aco_builder_track', track) },
    'acoBuilder.msrType'(v) { localStorage.setItem('aco_builder_msr_type', v) },
    'acoBuilder.qualityMet'(v) { localStorage.setItem('aco_builder_quality_met', String(v)) },
    'acoBuilder.targetPy'(v) { localStorage.setItem('aco_builder_target_py', String(v)) },
  },
  methods: {
    onSidebarClick(item) {
      this.activeTab = item.id
    },
    clearFilter(key) {
      this.practiceFilter[key] = ''
      this.fetchPractices()
    },
    async fetchTagDimensions() {
      try {
        const response = await this.$axios.get('/api/growth/tag-dimensions/')
        this.tagDimensions = response.data.dimensions
        this.tagGroups = response.data.groups
        this.tagDisplayRules = response.data.display_rules
        // Initialize filter keys from config
        for (const dim of this.tagDimensions) {
          if (!(dim.key in this.practiceFilter)) {
            this.practiceFilter[dim.key] = ''
          }
        }
      } catch (err) {
        console.error('Failed to load tag dimensions', err)
      }
    },
    applyTagFilter({ key, value }) {
      this.practiceFilter[key] = value
      this.fetchPractices()
    },

    // Practices
    buildFilterParams() {
      const f = this.practiceFilter
      const params = {
        state: f.state || undefined,
        search: f.search || undefined,
        min_benes: f.minBenes || undefined,
        max_benes: f.maxBenes || undefined,
        limit: this.practicePagination.limit,
        offset: this.practicePagination.offset,
      }
      // Dynamic tag filters from config
      for (const dim of this.tagDimensions) {
        if (f[dim.key]) params[dim.key] = f[dim.key]
      }
      return params
    },
    async fetchPractices() {
      this.practicesLoading = true
      this.practicesError = null
      this.practicePagination.offset = 0
      try {
        const response = await this.$axios.get('/api/growth/practices/', {
          params: this.buildFilterParams()
        })
        this.practices = response.data.results
        this.practicePagination.total = response.data.total
      } catch (err) {
        this.practicesError = err.response?.data?.detail || 'Failed to load practices'
      } finally {
        this.practicesLoading = false
      }
    },
    async fetchPracticesPage() {
      this.practicesLoading = true
      try {
        const response = await this.$axios.get('/api/growth/practices/', {
          params: this.buildFilterParams()
        })
        this.practices = response.data.results
        this.practicePagination.total = response.data.total
      } catch (err) {
        this.practicesError = err.response?.data?.detail || 'Failed to load practices'
      } finally {
        this.practicesLoading = false
      }
    },
    clearFilters() {
      const reset = { state: '', search: '', minBenes: '', maxBenes: '' }
      for (const dim of this.tagDimensions) reset[dim.key] = ''
      this.practiceFilter = reset
      this.fetchPractices()
    },
    selectPractice(practice) {
      this.selectedPractice = practice
    },
    onPracticeTabChange(tab) {
      const p = this.selectedPractice
      if (!p) return
      if (tab === 'Peer Benchmark' && !this.practicePeerBenchmark && !this.practicePeerBenchmarkLoading && p.TIN_ID) {
        this.fetchPracticePeerBenchmark(p.TIN_ID)
      } else if (tab === 'Primary Care Model' && !this.practiceFinancial && !this.practiceFinancialLoading) {
        this.fetchPracticeFinancial(p.PAC_ID)
      }
    },
    async fetchPracticeDetail(pacId) {
      this.practiceDetailLoading = true
      this.practiceDetail = null
      try {
        const response = await this.$axios.get(`/api/growth/practices/${pacId}/`)
        this.practiceDetail = response.data
      } catch (err) {
        console.error('Failed to load practice detail', err)
      } finally {
        this.practiceDetailLoading = false
      }
    },
    async fetchPracticeInsights(pacId) {
      this.practiceInsightsLoading = true
      this.practiceInsights = null
      try {
        const response = await this.$axios.get(`/api/growth/practices/${pacId}/insights/`)
        this.practiceInsights = response.data
      } catch (err) {
        console.error('Failed to load practice insights', err)
      } finally {
        this.practiceInsightsLoading = false
      }
    },
    prevPracticePage() {
      if (this.practicePagination.offset > 0) {
        this.practicePagination.offset -= this.practicePagination.limit
        this.fetchPracticesPage()
      }
    },
    nextPracticePage() {
      if (this.practicePagination.offset + this.practicePagination.limit < this.practicePagination.total) {
        this.practicePagination.offset += this.practicePagination.limit
        this.fetchPracticesPage()
      }
    },
    async fetchPracticeFinancial(pacId) {
      this.practiceFinancialLoading = true
      this.practiceFinancial = null
      try {
        const response = await this.$axios.get(`/api/growth/practices/${pacId}/financial/`)
        this.practiceFinancial = response.data
      } catch (err) {
        console.error('Failed to load financial assessment', err)
      } finally {
        this.practiceFinancialLoading = false
      }
    },

    async refetchPeerBenchmarkWithDimensions(activeDimensions) {
      if (!this.selectedPractice?.TIN_ID) return
      const pacId = this.selectedPractice.TIN_ID
      this.practicePeerBenchmarkLoading = true
      try {
        const dims = activeDimensions.join(',')
        const response = await this.$axios.get(`/api/growth/practices/${pacId}/peer-benchmark/?dimensions=${dims}`)
        this.practicePeerBenchmark = response.data
      } catch (err) {
        console.error('Failed to refetch peer benchmark', err)
      } finally {
        this.practicePeerBenchmarkLoading = false
      }
    },
    async fetchPracticePeerBenchmark(pacId) {
      this.practicePeerBenchmarkLoading = true
      this.practicePeerBenchmark = null
      try {
        const response = await this.$axios.get(`/api/growth/practices/${pacId}/peer-benchmark/`)
        this.practicePeerBenchmark = response.data
      } catch (err) {
        // Silently fail — peer benchmark is optional
      } finally {
        this.practicePeerBenchmarkLoading = false
      }
    },

    // ACO Builder
    isInAcoBuilderRoster(tinId) {
      return this.acoBuilder.roster.some(item => item.tinId === tinId)
    },
    toggleAcoBuilderRoster(practice) {
      if (!practice.TIN_ID) return
      if (this.isInAcoBuilderRoster(practice.TIN_ID)) {
        this.removeFromAcoBuilderRoster(practice.TIN_ID)
      } else {
        this.addToAcoBuilderRoster(practice)
      }
    },
    addToAcoBuilderRoster(practice) {
      if (!practice.TIN_ID || this.isInAcoBuilderRoster(practice.TIN_ID)) return
      const tinName = practice.TIN_NAME && practice.TIN_NAME.toLowerCase() !== 'not applicable'
        ? practice.TIN_NAME
        : practice.NAME
      this.acoBuilder.roster.push({
        tinId: practice.TIN_ID,
        name: tinName,
        pacId: practice.PAC_ID,
      })
      this.acoBuilder.projection = null
      this.acoBuilder.error = null
      this.acoBuilder.warning = null
    },
    removeFromAcoBuilderRoster(tinId) {
      this.acoBuilder.roster = this.acoBuilder.roster.filter(item => item.tinId !== tinId)
      this.acoBuilder.projection = null
      this.acoBuilder.error = null
      this.acoBuilder.warning = null
    },
    clearAcoBuilderRoster() {
      this.acoBuilder.roster = []
      this.acoBuilder.projection = null
      this.acoBuilder.error = null
      this.acoBuilder.warning = null
    },
    updateAcoBuilderYear(year) {
      this.acoBuilder.year = year
      this.acoBuilder.projection = null
      this.acoBuilder.error = null
      this.acoBuilder.warning = null
    },
    updateAcoBuilderTrack(track) {
      this.acoBuilder.track = track
      this.acoBuilder.projection = null
      this.acoBuilder.error = null
      this.acoBuilder.warning = null
    },
    updateAcoBuilderMsrType(msrType) {
      this.acoBuilder.msrType = msrType
      this.acoBuilder.projection = null
      this.acoBuilder.error = null
      this.acoBuilder.warning = null
    },
    updateAcoBuilderTargetPy(targetPy) {
      this.acoBuilder.targetPy = targetPy
      this.acoBuilder.projection = null
      this.acoBuilder.error = null
      this.acoBuilder.warning = null
    },
    updateAcoBuilderQuality(qualityMet) {
      this.acoBuilder.qualityMet = qualityMet
      this.acoBuilder.projection = null
      this.acoBuilder.error = null
      this.acoBuilder.warning = null
    },
    async calculateProjection() {
      if (this.acoBuilder.roster.length === 0) return
      this.acoBuilder.loading = true
      this.acoBuilder.error = null
      this.acoBuilder.warning = null
      this.acoBuilder.projection = null
      try {
        const response = await this.$axios.post('/api/growth/aco-projection/', {
          billing_entity_ids: this.acoBuilder.roster.map(item => item.tinId),
          track: this.acoBuilder.track,
          year: this.acoBuilder.year,
          methodology: 'MSSP',
          alignment: 'Retrospective',
          msr_type: this.acoBuilder.msrType,
          quality_met: this.acoBuilder.qualityMet,
          target_py: this.acoBuilder.targetPy,
        })
        if (response.data.warning) {
          this.acoBuilder.warning = response.data.warning
        } else if (response.data.error) {
          this.acoBuilder.error = response.data.error
        } else {
          this.acoBuilder.projection = response.data
        }
      } catch (err) {
        this.acoBuilder.error = err.response?.data?.detail || 'Failed to calculate projection'
      } finally {
        this.acoBuilder.loading = false
      }
    },
    exportRoster() {
      if (this.acoBuilder.roster.length === 0) return
      const headers = ['TIN_ID', 'Name', 'PAC_ID']
      const rows = this.acoBuilder.roster.map(item => [
        item.tinId,
        `"${(item.name || '').replace(/"/g, '""')}"`,
        item.pacId,
      ])
      let csv = headers.join(',') + '\n'
      csv += rows.map(row => row.join(',')).join('\n')
      if (this.acoBuilder.projection) {
        csv += '\n\n# Projection Summary\n'
        csv += `Track,${this.acoBuilder.track}\n`
        csv += `Total Beneficiaries,${this.acoBuilder.projection.roster?.total_benes || 0}\n`
        csv += `Weighted PMPY,$${this.acoBuilder.projection.performance?.weighted_pmpy || 0}\n`
        csv += `Risk-Adjusted Benchmark,$${this.acoBuilder.projection.benchmark?.risk_adjusted_benchmark_pmpy || 0}\n`
        csv += `Projected Savings %,${((this.acoBuilder.projection.projection?.savings_pct || 0) * 100).toFixed(1)}%\n`
        csv += `MSR,${((this.acoBuilder.projection.projection?.msr || 0) * 100).toFixed(1)}%\n`
        csv += `Exceeds MSR,${this.acoBuilder.projection.projection?.exceeds_msr ? 'Yes' : 'No'}\n`
      }
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `aco_roster_${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      URL.revokeObjectURL(url)
    },

    // Pattern Explorer
    async fetchPatterns() {
      this.patternsLoading = true
      this.patternsError = null
      try {
        const response = await this.$axios.get('/api/growth/patterns/')
        this.patternsData = response.data
      } catch (err) {
        this.patternsError = err.response?.data?.detail || 'Failed to load patterns'
      } finally {
        this.patternsLoading = false
      }
    },
    selectDimension(payload) {
      this.cancelEditingRule()
      this.tagPractices = { loading: false, practices: [], total: 0, offset: 0, limit: 20 }
      this.selectedItem = {
        type: 'dimension',
        column: payload.dim.column,
        label: payload.dim.label,
        family: payload.family || '',
        values: payload.dim.values
      }
    },
    selectLayer2Flag(flag) {
      this.cancelEditingRule()
      this.selectedItem = {
        type: 'layer2',
        tag_id: flag.tag_id,
        column: flag.column,
        label: flag.label,
        category: flag.category,
        rule: flag.rule,
        layer1_tags: flag.layer1_tags,
        true_count: flag.true_count
      }
      this.fetchTagPractices(flag.column)
    },
    selectLayer3Archetype(archetype) {
      this.cancelEditingRule()
      this.selectedItem = {
        type: 'layer3',
        tag_id: archetype.tag_id,
        column: archetype.column,
        label: archetype.label,
        rule: archetype.rule,
        layer2_flags: archetype.layer2_flags,
        true_count: archetype.true_count
      }
      this.fetchTagPractices(archetype.column)
    },
    startEditingRule(item) {
      this.ruleEditor = {
        editing: true,
        tagId: item.tag_id,
        expression: item.rule || '',
        loading: false,
        error: null,
        preview: null,
      }
    },
    cancelEditingRule() {
      this.ruleEditor = {
        editing: false,
        tagId: null,
        expression: '',
        loading: false,
        error: null,
        preview: null,
      }
    },
    async previewRule() {
      this.ruleEditor.loading = true
      this.ruleEditor.error = null
      this.ruleEditor.preview = null
      try {
        const response = await this.$axios.post('/api/growth/patterns/', {
          action: 'preview',
          tag_id: this.ruleEditor.tagId,
          sql_expression: this.ruleEditor.expression,
        })
        this.ruleEditor.preview = response.data
      } catch (err) {
        this.ruleEditor.error = err.response?.data?.detail || 'Preview failed'
      } finally {
        this.ruleEditor.loading = false
      }
    },
    async saveRule() {
      this.ruleEditor.loading = true
      this.ruleEditor.error = null
      try {
        await this.$axios.post('/api/growth/patterns/', {
          action: 'update_rule',
          tag_id: this.ruleEditor.tagId,
          sql_expression: this.ruleEditor.expression,
        })
        await this.$axios.post('/api/growth/patterns/', {
          action: 'apply',
        })
        this.cancelEditingRule()
        await this.fetchPatterns()
        if (this.selectedItem) {
          const tagId = this.selectedItem.tag_id
          const updatedFlag = this.patternsData?.layer2Flags?.find(f => f.tag_id === tagId)
          const updatedArch = this.patternsData?.layer3Archetypes?.find(a => a.tag_id === tagId)
          if (updatedFlag) this.selectLayer2Flag(updatedFlag)
          else if (updatedArch) this.selectLayer3Archetype(updatedArch)
        }
      } catch (err) {
        this.ruleEditor.error = err.response?.data?.detail || 'Save failed'
      } finally {
        this.ruleEditor.loading = false
      }
    },
    async fetchTagPractices(tagColumn, reset = true) {
      if (reset) {
        this.tagPractices.offset = 0
        this.tagPractices.practices = []
      }
      this.tagPractices.loading = true
      try {
        const response = await this.$axios.get('/api/growth/practices/', {
          params: {
            tag_column: tagColumn,
            limit: this.tagPractices.limit,
            offset: this.tagPractices.offset,
          }
        })
        this.tagPractices.practices = response.data.results
        this.tagPractices.total = response.data.total
      } catch (err) {
        console.error('Failed to fetch tag practices:', err)
      } finally {
        this.tagPractices.loading = false
      }
    },
    async loadMoreTagPractices() {
      if (!this.selectedItem?.column) return
      this.tagPractices.offset += this.tagPractices.limit
      this.tagPractices.loading = true
      try {
        const response = await this.$axios.get('/api/growth/practices/', {
          params: {
            tag_column: this.selectedItem.column,
            limit: this.tagPractices.limit,
            offset: this.tagPractices.offset,
          }
        })
        this.tagPractices.practices = [...this.tagPractices.practices, ...response.data.results]
      } catch (err) {
        console.error('Failed to load more:', err)
      } finally {
        this.tagPractices.loading = false
      }
    },
    navigateToPractice(practice) {
      this.activeTab = 'directory'
      this.practiceFilter.search = practice.TIN_ID || practice.NAME
      this.fetchPractices()
    },
  },
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
